import { useEffect, useState, useCallback } from 'react';
import { ApiClient } from '@locus-taxy/encore-ui-network';

interface UsePhotoUrlParams {
  fileId: string | null | undefined;
  clientId: string;
}

interface UsePhotoUrlReturn {
  photoUrl: string | null;
  loading: boolean;
  error: string | null;
}

export function usePhotoUrl({
  fileId,
  clientId,
}: UsePhotoUrlParams): UsePhotoUrlReturn {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPhoto = useCallback(async () => {
    if (!fileId) {
      setPhotoUrl(null);
      setLoading(false);
      setError(null);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const apiClient = ApiClient.getInstance();
      const endpoint = `/api/v1/view/clients/${clientId}/files/${fileId}`;

      const response = await apiClient.get<Blob>(endpoint, {
        responseType: 'blob',
      });

      const blob = response instanceof Blob ? response : (response as any);

      if (!(blob instanceof Blob)) {
        throw new Error('Invalid response format - expected Blob');
      }

      if (blob.size === 0) {
        throw new Error('Received empty image file');
      }

      const objectUrl = URL.createObjectURL(blob);
      setPhotoUrl(objectUrl);

      return objectUrl;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load photo';
      setError(errorMessage);
      setPhotoUrl(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fileId, clientId]);

  useEffect(() => {
    let objectUrl: string | null = null;

    fetchPhoto().then((url) => {
      objectUrl = url;
    });

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fetchPhoto]);

  return {
    photoUrl,
    loading,
    error,
  };
}
